/* Ruby Rose — right-side overlay panels.

   Click to open, and nothing but an explicit dismiss closes it: the X, the
   scrim, Escape, or clicking the same icon again.

   These used to open on hover after a 120ms intent delay and close 250ms after
   the pointer left. It made the header feel trigger-happy — brushing past an
   icon on the way to another threw a full-height overlay over the page — and it
   gave the site two different dismissal models depending on whether you had
   arrived by pointer or by tap. Hover is gone entirely; one model now. */

(function () {
  // Header control -> panel view. Order matters: first match wins.
  const TRIGGERS = [
    { view: "wishlist", selector: ".rr-header__wishlist" },
    { view: "bag", selector: "cart-drawer-component" },
    { view: "language", selector: ".dropdown-localization" },
    { view: "account", selector: ".account-button" },
    { view: "search", selector: "search-button.search-action" },
    { view: "menu", selector: ".header__drawer" },
  ];

  let root, panel, scrim, titleEl, lastTrigger = null;
  let currentView = null;

  /* close() hides the panel only once its slide-out has finished, with a timer
     as the fallback for when the transition is suppressed. Reopening inside
     that window used to let the stale teardown fire over the top of the new
     panel — it opened and then vanished on its own. Both the timer and the
     listener are tracked so open() can cancel them. */
  let teardownTimer = null;
  let teardown = null;

  function cancelTeardown() {
    clearTimeout(teardownTimer);
    teardownTimer = null;
    if (teardown) {
      panel.removeEventListener("transitionend", teardown);
      teardown = null;
    }
  }

  const views = () => [...root.querySelectorAll("[data-rr-view]")];

  function money(cents, currency) {
    return `${currency} ${(cents / 100).toFixed(2)}`;
  }

  /* ---------------- Bag ---------------- */
  async function renderBag() {
    const body = root.querySelector("[data-rr-bag-body]");
    const summary = root.querySelector("[data-rr-bag-summary]");
    const subtotal = root.querySelector("[data-rr-bag-subtotal]");
    const cont = root.querySelector("[data-rr-continue]");
    if (!body) return;

    let cart;
    try {
      cart = await (await fetch("/cart.js", { headers: { Accept: "application/json" } })).json();
    } catch {
      return; // leave the existing empty state in place
    }

    /* Adds go straight to /cart/add.js, so Horizon never re-renders its own
       count bubble — which is what the header badge reads. This fetch is the
       freshest count anyone has, so publish it. */
    document.dispatchEvent(
      new CustomEvent("ruby:cart:count", { detail: { count: cart.item_count } })
    );

    const emptyMsg = body.querySelector("[data-rr-bag-empty]");
    const emptyText = emptyMsg ? emptyMsg.textContent : "Your cart is currently empty.";

    if (!cart.item_count) {
      body.textContent = "";
      const note = document.createElement("p");
      note.className = "rr-panel__lead";
      note.dataset.rrBagEmpty = "";
      note.textContent = emptyText;
      body.appendChild(note);
      summary.hidden = true;
      // Cleared as well as hidden: leaving the last total sitting in the DOM
      // means anything that later unhides the summary shows a stale number.
      if (subtotal) subtotal.textContent = "";
      if (cont) cont.hidden = false;
      return;
    }

    /* Built as nodes rather than an HTML string: product titles and variant
       names are store data going straight into markup otherwise. */
    body.textContent = "";
    cart.items.forEach((item) => body.appendChild(bagLine(item, cart.currency)));

    subtotal.textContent = money(cart.total_price, cart.currency);
    summary.hidden = false;
    if (cont) cont.hidden = true;
  }

  /* One cart line, with its quantity controls.
     Keyed on item.key, not the variant id: the same variant can occupy two
     lines (different properties or selling plans) and an id would edit the
     wrong one. */
  function bagLine(item, currency) {
    const line = document.createElement("div");
    line.className = "rr-panel__line";
    line.dataset.rrLine = item.key;

    if (item.image) {
      const img = document.createElement("img");
      img.src = item.image.replace(/(\.[a-z]+)(\?|$)/i, "_128x$1$2");
      img.alt = "";
      img.loading = "lazy";
      line.appendChild(img);
    } else {
      line.appendChild(document.createElement("span"));
    }

    const middle = document.createElement("div");

    const title = document.createElement("a");
    title.className = "rr-panel__line-title";
    title.href = item.url || "#";
    title.textContent = item.product_title;
    middle.appendChild(title);

    if (item.variant_title) {
      const variant = document.createElement("p");
      variant.className = "rr-panel__line-meta";
      variant.textContent = item.variant_title;
      middle.appendChild(variant);
    }

    const controls = document.createElement("div");
    controls.className = "rr-panel__qty";

    const step = (label, delta, disabled) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "rr-panel__qty-step";
      b.dataset.rrQty = String(delta);
      b.setAttribute("aria-label", `${label} ${item.product_title}`);
      b.textContent = delta > 0 ? "+" : "−";
      b.disabled = !!disabled;
      return b;
    };

    // Minus is disabled at one rather than removing the line: emptying a line
    // by nudging a stepper is easy to do by accident, so removal is the bin.
    controls.appendChild(step("Decrease quantity of", -1, item.quantity <= 1));

    const count = document.createElement("span");
    count.className = "rr-panel__qty-value";
    count.setAttribute("aria-live", "polite");
    count.textContent = String(item.quantity);
    controls.appendChild(count);

    controls.appendChild(step("Increase quantity of", 1, false));

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "rr-panel__line-remove";
    remove.dataset.rrRemoveLine = item.key;
    remove.setAttribute("aria-label", `Remove ${item.product_title} from your bag`);
    remove.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
    controls.appendChild(remove);

    middle.appendChild(controls);
    line.appendChild(middle);

    const price = document.createElement("span");
    price.className = "rr-panel__line-price";
    price.textContent = money(item.final_line_price, currency);
    line.appendChild(price);

    return line;
  }

  /* Quantity changes go through /cart/change.js and the panel re-renders from
     the response, so the subtotal and the header badge cannot drift from the
     real cart. The panel is locked while a change is in flight — double-firing
     a stepper would otherwise queue two changes against one starting value. */
  let cartBusy = false;

  async function changeLine(key, quantity) {
    if (cartBusy) return;
    cartBusy = true;
    const bagBody = root.querySelector("[data-rr-bag-body]");
    bagBody?.setAttribute("data-rr-busy", "");

    try {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: quantity }),
      });
    } catch {
      // Leave the panel as it stands; the next open re-reads the real cart.
    } finally {
      cartBusy = false;
      bagBody?.removeAttribute("data-rr-busy");
      await renderBag();
    }
  }

  /* ---------------- Open / close ---------------- */
  function open(view, trigger) {
    if (!root) return;
    cancelTeardown();
    lastTrigger = trigger || null;
    currentView = view;

    let heading = "";
    views().forEach((v) => {
      const match = v.dataset.rrView === view;
      v.hidden = !match;
      if (match) heading = v.dataset.rrHeading || "";
    });
    titleEl.textContent = heading;

    root.hidden = false;
    // Force a reflow so the transition starts from the off-screen position.
    // Deliberately not requestAnimationFrame: that never fires in a background
    // or non-compositing tab, which would leave the panel open but slid out of
    // view with an invisible scrim.
    void root.offsetHeight;
    root.classList.add("is-open");
    document.documentElement.style.overflow = "hidden";

    if (view === "bag") renderBag();

    const focusable = panel.querySelector(
      'input, select, a[href], button:not([data-rr-close])'
    );
    (focusable || panel.querySelector("[data-rr-close]"))?.focus({ preventScroll: true });
  }

  function close() {
    if (!root || root.hidden) return;
    currentView = null;
    root.classList.remove("is-open");
    document.documentElement.style.overflow = "";
    cancelTeardown();
    teardown = () => {
      root.hidden = true;
      cancelTeardown();
    };
    panel.addEventListener("transitionend", teardown);
    teardownTimer = setTimeout(teardown, 400); // if the transition is suppressed
    lastTrigger?.focus?.({ preventScroll: true });
    lastTrigger = null;
  }

  const isOpen = () => root && !root.hidden;

  /* ---------------- Header wiring ---------------- */
  function wireTriggers() {
    const header = document.querySelector("#header-component");
    if (!header) return;

    TRIGGERS.forEach(({ view, selector }) => {
      header.querySelectorAll(selector).forEach((el) => {
        if (el.dataset.rrOpens) return;
        /* Signed-out visitors get the hover account panel instead
           (snippets/ruby-account-panel.liquid, assets/ruby-account.js). It
           lives inside .account-button, so a capture listener here would also
           swallow clicks on its email field and Sign in button. */
        if (view === "account" && el.querySelector("[data-rr-acct]")) return;
        el.dataset.rrOpens = view;

        // Capture phase so Horizon's own drawer/modal handlers never fire.
        el.addEventListener(
          "click",
          (event) => {
            event.preventDefault();
            /* Immediate, not plain stopPropagation: plain only stops other
               nodes, and two listeners on this very element would both still
               run — the first opening the panel and the second reading it as
               the toggle and closing it again, all in one click. */
            event.stopImmediatePropagation();

            // Belt and braces for the same hazard, since a duplicate binding
            // added before this one would run ahead of the guard above.
            if (event.rrHandled) return;
            event.rrHandled = true;

            // Same icon again acts as the cancel, so the control that opened
            // the panel can also put it away.
            if (isOpen() && currentView === view) {
              close();
              return;
            }

            open(view, el);
          },
          true
        );
      });
    });
  }

  /* ---------------- Horizon's own cart drawer ----------------
     The bag panel replaces it, so only one of the two may ever appear.
     Intercepting the click was not enough: Horizon binds its opener through a
     delegated document-level listener, so depending on phase it can run before
     ours, and it ignores synthetic clicks — which is why the doubled overlay
     showed up for real customers but never in scripted testing.

     So the dialog itself is neutered rather than the click. Whatever calls it,
     from any code path, opens our bag instead. The attribute observer is the
     backstop for anything that sets `open` directly, and re-running this on
     header mutations covers Horizon re-rendering the drawer after a cart
     change, which would otherwise hand back a fresh, un-neutered dialog. */
  function supersedeCartDrawer() {
    document.querySelectorAll("cart-drawer-component dialog").forEach((dialog) => {
      if (dialog.dataset.rrSuperseded) return;
      dialog.dataset.rrSuperseded = "1";

      const showBagInstead = () => {
        if (!isOpen() || currentView !== "bag") open("bag", null);
      };

      dialog.showModal = showBagInstead;
      dialog.show = showBagInstead;

      new MutationObserver(() => {
        if (!dialog.hasAttribute("open")) return;
        dialog.removeAttribute("open");
        showBagInstead();
      }).observe(dialog, { attributes: true, attributeFilter: ["open"] });
    });
  }

  /* ---------------- Add to cart -> open the bag ---------------- */
  function wireAddToCart() {
    document.addEventListener(
      "submit",
      async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!/\/cart\/add/.test(form.getAttribute("action") || "")) return;

        event.preventDefault();
        event.stopPropagation();

        const button = form.querySelector('[name="add"]');
        button?.setAttribute("disabled", "");
        try {
          await fetch("/cart/add.js", { method: "POST", body: new FormData(form) });
        } finally {
          button?.removeAttribute("disabled");
        }
        open("bag", null);
      },
      true
    );
  }

  function init() {
    root = document.querySelector("[data-rr-panels]");
    if (!root) return;
    panel = root.querySelector("[data-rr-panel]");
    scrim = root.querySelector("[data-rr-scrim]");
    titleEl = root.querySelector("[data-rr-panel-title]");

    // The three ways out: the scrim, the X, and Escape below.
    scrim.addEventListener("click", close);
    root.querySelector("[data-rr-close]").addEventListener("click", close);

    /* Cart edits. Delegated, because the lines are rebuilt on every change. */
    root.addEventListener("click", (event) => {
      const drop = event.target.closest("[data-rr-remove-line]");
      if (drop) {
        event.preventDefault();
        changeLine(drop.dataset.rrRemoveLine, 0);
        return;
      }

      const step = event.target.closest("[data-rr-qty]");
      if (!step || step.disabled) return;
      event.preventDefault();

      const line = step.closest("[data-rr-line]");
      const value = line?.querySelector(".rr-panel__qty-value");
      if (!line || !value) return;

      const next = parseInt(value.textContent, 10) + Number(step.dataset.rrQty);
      if (next < 1) return;
      changeLine(line.dataset.rrLine, next);
    });

    document.addEventListener("keydown", (event) => {
      if (!isOpen()) return;
      if (event.key === "Escape") { close(); return; }
      if (event.key !== "Tab") return;

      // Focus trap
      const items = [...panel.querySelectorAll(
        'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )].filter((el) => el.offsetParent !== null && !el.disabled);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Language select submits its Shopify localization form on change.
    root.querySelector("[data-rr-locale]")?.addEventListener("change", (event) => {
      event.target.form?.submit();
    });

    wireTriggers();
    wireAddToCart();
    supersedeCartDrawer();

    // The header re-renders parts of itself after mount; re-wire when it does.
    const header = document.querySelector("#header-component");
    if (header) {
      new MutationObserver(() => {
        wireTriggers();
        supersedeCartDrawer();
      }).observe(header, { childList: true, subtree: true });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
